import { ColDef, ColGroupDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { GridLoadingIndicator } from "components/widgets";
import { useEffect, useMemo, useRef, useState } from "react";
import CiqAgSearch from "components/ciq-ag-search";
import CiqLogPageHeader from "components/ciq-log-page-header";
import {
  MUTATION_UPDATE_PROJECT_FEATURE_FLAG,
  MUTATION_UPDATE_SUBSCRIPTION_FEATURE_FLAG
} from "services/graphQL/mutations";
import { useMutation } from "@apollo/client";
import { useFeatureFlags } from "../organization-details/use-feature-flags";
import {
  ProjectSwitchCellRenderer,
  SubscriptionSwitchCellRenderer
} from "./cell-renderers";

const headerTooltipMap: any = {
  MATERIAL_EXTRACTION:
    "Shows Extracted Tab in Materials and 'Extract from Drawings' button.",
  SMART_LINKING:
    "Onboarding Automation - Smart suggestions of Activities in Material linking page. Enabling this for a project will also enable Onboarding Automation in Create Project for all new projects to be created in the Subscription.",
  PROCUREMENT_SCHEDULE: "Timeline in Project Dashboard.",
  REQUEST_LEAD_TIME: "Send request to SCs from Material Log",
  BUYOUT: "Bid & Design Package",
  UPCOMING_MILESTONES_NOTIFICATION: "Procurement Follow-up Report",
  MATERIAL_PROGRESS_DASHBOARD: "Appears in Analytics tab of Project Dashboard.",
  MATERIAL_READINESS_DASHBOARD:
    "Appears in Analytics tab of Project Dashboard.",
  CUSTOM_REPORT: "Custom Material Report.",
  SUBMITTAL_PROGRESS_DASHBOARD:
    "Appears in Analytics tab of Project Dashboard.",
  USAGE_SUMMARY_DASHBOARD: "Appears in Analytics tab at Subscription Level",
  EXECUTIVE_OVERVIEW_DASHBOARD: "Appears in Analytics tab at Subscription Level"
};

const toTitleCase = (text: string) =>
  text
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// Detail cell renderer for projects under each subscription
function ProjectDetailCellRenderer({ data, context }: any) {
  const [detailData, setDetailData] = useState<any[]>([]);
  const detailGridRef = useRef<AgGridReact>(null);

  const [updateProjectFeatureFlagMutation] = useMutation(
    MUTATION_UPDATE_PROJECT_FEATURE_FLAG
  );

  // Extract unique project-level features
  const projectFeatures = useMemo(() => {
    if (!data?.projects) return [];

    const featuresSet = new Set<string>();
    data.projects.forEach((project: any) => {
      project.project_product_entitlements?.forEach((entitlement: any) => {
        if (entitlement.product_entitlement?.feature) {
          featuresSet.add(entitlement.product_entitlement.feature);
        }
      });
    });

    return Array.from(featuresSet).sort();
  }, [data?.projects]);

  const defaultColDef = useMemo(() => {
    return {
      sortable: false,
      editable: false,
      draggable: false,
      filter: false,
      resizable: true,
      menuTabs: []
    };
  }, []);

  // Generate dynamic column definitions for project features
  const detailColumnDefs: (ColDef | ColGroupDef)[] = useMemo(() => {
    const dynamicFeatureColumns: ColGroupDef[] = projectFeatures.map(
      (feature) => ({
        headerName: feature?.replaceAll("_", " "),
        headerTooltip: headerTooltipMap[feature] ?? toTitleCase(feature),
        minWidth: 250,
        draggable: false,
        children: [
          {
            field: `${feature}.enabled`,
            headerName: "Enable/Disable",
            minWidth: 150,
            cellRenderer: ProjectSwitchCellRenderer
          }
        ]
      })
    );

    return [
      {
        field: "name",
        headerName: "Project",
        minWidth: 150,
        cellRenderer: "agGroupCellRenderer",
        getQuickFilterText: (params) => params.value,
        pinned: "left",
        lockPosition: "left",
        draggable: false
      },
      {
        headerName: "Project Level Features",
        minWidth: 250,
        draggable: false,
        children: dynamicFeatureColumns
      } as ColGroupDef
    ];
  }, [projectFeatures]);

  useEffect(() => {
    // Map projects data with flattened entitlements
    if (data?.projects) {
      const transformedProjects = data.projects.map((project: any) => {
        const rowData: any = {
          id: project.id,
          name: project.name,
          project_product_entitlements: project.project_product_entitlements
        };

        // Flatten entitlements into direct properties
        project.project_product_entitlements?.forEach((entitlement: any) => {
          const feature = entitlement.product_entitlement?.feature;
          if (feature) {
            rowData[feature] = {
              enabled: entitlement.enabled,
              enableForNewProjects: entitlement.enable_for_new_project
            };
          }
        });

        return rowData;
      });

      setDetailData(transformedProjects);
    }
  }, [data?.projects]);

  // Handler for updating project entitlements
  const updateProjectFeatureFlag = async (variables: {
    projectId: string;
    featureFlagId: number;
    featureFlag: boolean;
  }) => {
    try {
      const result = await updateProjectFeatureFlagMutation({ variables });
      return result;
    } catch (err) {
      console.error("Failed to update project feature flag:", err);
      throw err;
    }
  };

  return (
    <div
      className="ag-theme-alpine"
      style={{
        paddingLeft: "5%",
        paddingRight: "5%",
        width: "100%",
        height: "350px"
      }}
    >
      <AgGridReact
        ref={detailGridRef}
        defaultColDef={defaultColDef}
        columnDefs={detailColumnDefs}
        rowData={detailData}
        headerHeight={42}
        rowHeight={42}
        quickFilterText={context?.searchText}
        tooltipShowMode="standard"
        context={{
          searchText: context?.searchText,
          updateProjectFeatureFlag
        }}
      />
    </div>
  );
}

interface FeatureFlagSettingsProps {
  organization_id: string;
}

function FeatureFlagSettings({ organization_id }: FeatureFlagSettingsProps) {
  const gridRef = useRef<AgGridReact>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [searchText, setSearchText] = useState<string>("");

  // Fetch feature flags data using the custom hook
  const {
    data: subscriptionData,
    loading,
    error
  } = useFeatureFlags({
    organizationId: organization_id
  });

  // Mutations for updating entitlements

  const [updateSubscriptionFeatureFlagMutation] = useMutation(
    MUTATION_UPDATE_SUBSCRIPTION_FEATURE_FLAG
  );

  const updateSubscriptionFeatureFlag = async (variables: {
    subscriptionId: string;
    featureFlagId: number;
    featureFlag: boolean;
  }) => {
    try {
      const result = await updateSubscriptionFeatureFlagMutation({ variables });
      return result;
    } catch (err) {
      console.error("Failed to update subscription feature flag:", err);
      throw err;
    }
  };

  // Extract all unique features from subscription_product_entitlements
  const allUniqueFeatures = useMemo(() => {
    if (!subscriptionData?.subscription) return [];

    const featuresSet = new Set<string>();
    subscriptionData.subscription.forEach((subscription) => {
      subscription.subscription_product_entitlements?.forEach((entitlement) => {
        if (entitlement.product_entitlement?.feature) {
          featuresSet.add(entitlement.product_entitlement.feature);
        }
      });
    });

    return Array.from(featuresSet).sort();
  }, [subscriptionData]);

  // Map subscription data with flattened entitlements for easy access
  const subscriptionFeatureFlags = useMemo(() => {
    return subscriptionData?.subscription?.map((subscription) => {
      const rowData: any = {
        id: subscription.id,
        name: subscription.name,
        projects: subscription.projects_owned,
        subscription_product_entitlements:
          subscription.subscription_product_entitlements
      };

      // Flatten entitlements into direct properties for grid columns
      subscription.subscription_product_entitlements?.forEach((entitlement) => {
        const feature = entitlement.product_entitlement?.feature;
        if (feature) {
          rowData[feature] = entitlement.enabled;
        }
      });

      return rowData;
    });
  }, [subscriptionData]);

  // Generate dynamic column definitions based on unique features
  const columnDefs: (ColDef | ColGroupDef)[] = useMemo(() => {
    const dynamicFeatureColumns: ColDef[] = allUniqueFeatures.map(
      (feature) => ({
        field: feature,
        headerName: feature?.replaceAll("_", " "),
        cellRenderer: SubscriptionSwitchCellRenderer,
        headerTooltip: headerTooltipMap[feature] ?? toTitleCase(feature)
      })
    );

    return [
      {
        field: "name",
        headerName: "Subscription",
        width: 250,
        pinned: "left",
        lockPosition: "left",
        cellRenderer: "agGroupCellRenderer",
        tooltipField: "name",
        getQuickFilterText: (params) => params.value,
        cellRendererParams: {
          suppressCount: true
        }
      },
      {
        headerName: "Subscription Level Features",
        children: dynamicFeatureColumns
      } as ColGroupDef
    ];
  }, [allUniqueFeatures]);

  const defaultColDef = useMemo(() => {
    return {
      sortable: false,
      editable: false,
      draggable: false,
      filter: false,
      resizable: true,
      menuTabs: []
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isGridReady]);

  const handleSearch = (text: string) => {
    setSearchText(text);
    if (gridRef.current?.api) {
      gridRef.current.api.onFilterChanged();
    }
  };

  const onFilterChanged = () => {
    if (gridRef.current?.api) {
      // Refresh and expand all rows after filter is applied
      setTimeout(() => {
        if (searchText) {
          gridRef.current?.api?.expandAll();
        }
      }, 100);
    }
  };

  const headerItems = [
    <CiqAgSearch
      key="FeatureFlags_search"
      gridRef={gridRef}
      placeholder="Search here."
      onSearch={handleSearch}
    />
  ];

  return (
    <div className="flex flex-col w-full h-[calc(100vh-136px)] bg-white">
      <CiqLogPageHeader
        {...{
          entityName: "Feature Flag Settings",
          gridRef,
          items: headerItems
        }}
      />
      <div className="grow w-full h-full ag-theme-alpine">
        <AgGridReact
          ref={gridRef}
          tooltipShowMode="standard"
          rowData={subscriptionFeatureFlags}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={42}
          rowHeight={42}
          loading={loading}
          loadingOverlayComponent={GridLoadingIndicator}
          onGridReady={() => {
            setGridReady(true);
            gridRef.current?.api.sizeColumnsToFit();
          }}
          onFilterChanged={onFilterChanged}
          masterDetail
          detailCellRenderer={ProjectDetailCellRenderer}
          detailRowHeight={360}
          isRowMaster={(dataItem) => {
            return dataItem.projects && dataItem.projects.length > 0;
          }}
          context={{
            searchText,
            updateSubscriptionFeatureFlag
          }}
          overlayNoRowsTemplate={
            error
              ? "Error loading feature flag data. Please try again."
              : "No feature flag data available."
          }
          isExternalFilterPresent={() => {
            return !!searchText;
          }}
          doesExternalFilterPass={(rowNode: any) => {
            if (!searchText) return true;

            const searchLower = searchText.toLowerCase().trim();
            const { data } = rowNode;

            if (!data) return false;

            // Check if subscription name matches
            const subscriptionName = data.name?.toLowerCase() || "";
            if (subscriptionName.includes(searchLower)) {
              return true;
            }

            // Check if any project name matches
            const projects = data.projects || [];
            return projects.some((project: any) => {
              const projectName = project.name?.toLowerCase() || "";
              return projectName.includes(searchLower);
            });
          }}
        />
      </div>
    </div>
  );
}

export default FeatureFlagSettings;
